import { supabaseClient } from './supabase';
import {
  applyLaCountyBlendToCombinedSeries,
  isLosAngelesCountyGeofips,
  shortLabelForCountyRestCarbonIntensity,
} from './losAngelesCountyGridCarbonIntensity';
import {
  buildCombinedCarbonIntensityPointsFirstPass,
  finalizeCombinedCarbonIntensitySeries,
  fetchCaliforniaStateElectricityCarbonIntensitySeriesFromTable,
  isCaliforniaStateElectricityGeofips,
  meanLseGco2eForYear,
  observedGco2eForYearFromEgridRows,
} from './carbonIntensitySeriesCore';

export interface CarbonIntensityDataPoint {
  year: number;
  value: number;
  source: 'observed' | 'interpolated' | 'extrapolated' | 'cca';
  provider: string;
}

export interface CarbonIntensityResult {
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string | string[];
      borderColor: string | string[];
      borderWidth: number;
    }>;
  };
  chartOptions: any;
  latestValue: number;
  latestYear: number;
  dataSource: string;
  providers: {
    cca: string[];
    egrid: string[];
  };
  seriesByYear?: CarbonIntensityDataPoint[];
}

// Fetch CCAS data for a location
async function fetchCCASData(geofips: string): Promise<any> {
  const { data, error } = await supabaseClient
    .from('ccas')
    .select('*')
    .eq('geofips', geofips)
    .single();
  
  if (error) throw error;
  return data;
}

// Fetch eGRID data for utility matches
async function fetchEGridData(utilityMatches: string[], years: number[]): Promise<any[]> {
  const { data, error } = await supabaseClient
    .from('egrid_utilities')
    .select('year, utility_name, gco2e_per_kwh, subregion')
    .in('year', years)
    .in('utility_name', utilityMatches.filter(match => match && match.trim() !== ''));

  if (error) throw error;
  return data || [];
}

// Fetch LSE carbon intensity data for CCA matches
async function fetchLSECarbonIntensityData(ccaMatches: string[], years: number[]): Promise<any[]> {
  const { data, error } = await supabaseClient
    .from('lse_carbon_intensity')
    .select('year, retail_supplier, gco2e_per_kwh')
    .in('year', years)
    .in('retail_supplier', ccaMatches.filter(match => match && match.trim() !== ''));
  
  if (error) throw error;
  return data || [];
}

/** Word-wrap long tooltip lines so Chart.js shows full source text (e.g. LA County blend). */
function wrapTooltipProviderLines(text: string, maxChars: number): string[] {
  const t = (text || '').trim();
  if (!t) return [];
  const words = t.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= maxChars) {
      cur = next;
    } else {
      if (cur) lines.push(cur);
      cur = w.length > maxChars ? `${w.slice(0, maxChars - 1)}…` : w;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [t];
}

type CombinedPoint = { value: number; source: string; provider: string };

const DEFAULT_STATE_CI_FALLBACK = 200;

async function assembleCarbonIntensityChartResult(
  years: number[],
  combinedData: CombinedPoint[],
  geofips: string,
  opts?: { stateFromElectricityTable?: boolean }
): Promise<CarbonIntensityResult> {
  const datasets: any[] = [];

  const laCountyHadCcaPreBlend =
    isLosAngelesCountyGeofips(geofips) && combinedData.some((d) => d.source === 'cca');
  const laCountyRestLabelsPreBlend = isLosAngelesCountyGeofips(geofips)
    ? Array.from(new Set(combinedData.map((d) => shortLabelForCountyRestCarbonIntensity(d.provider))))
    : [];

  if (isLosAngelesCountyGeofips(geofips)) {
    await applyLaCountyBlendToCombinedSeries(years, combinedData as any);
  }

  let ccaProviders: string[];
  let egridProviders: string[];
  if (opts?.stateFromElectricityTable) {
    ccaProviders = [];
    egridProviders = [
      'California statewide (geofips 6): values read from electricity_carbon_intensity (e.g. CAMX / weighted series).',
    ];
  } else if (isLosAngelesCountyGeofips(geofips)) {
    const restStr = laCountyRestLabelsPreBlend.join(' · ');
    egridProviders = [
      `LA County weighted blend: LADWP eGRID (utility 11208) + ${restStr} — shares from res+com kWh (City of LA vs county total).`,
    ];
    ccaProviders = laCountyHadCcaPreBlend
      ? [
          'Some years use a CCA Power Content Label for the non-LADWP share before blending (see yearly tooltip).',
        ]
      : [];
  } else {
    ccaProviders = Array.from(new Set(combinedData.filter((d) => d.source === 'cca').map((d) => d.provider)));
    egridProviders = Array.from(
      new Set(combinedData.filter((d) => d.source === 'observed').map((d) => d.provider))
    );
  }

  const singleDataset = {
    label: 'Carbon Intensity',
    data: combinedData.map((d) => d.value),
    clip: false as const,
    backgroundColor: combinedData.map((d) => {
      switch (d.source) {
        case 'cca':
          return 'rgba(16, 185, 129, 0.8)';
        case 'observed':
          return 'rgba(59, 130, 246, 0.8)';
        case 'interpolated':
          return 'rgba(234, 179, 8, 0.85)';
        case 'extrapolated':
          return 'rgba(239, 68, 68, 0.8)';
        default:
          return 'rgba(107, 114, 128, 0.8)';
      }
    }),
    borderColor: combinedData.map((d) => {
      switch (d.source) {
        case 'cca':
          return 'rgba(16, 185, 129, 1)';
        case 'observed':
          return 'rgba(59, 130, 246, 1)';
        case 'interpolated':
          return 'rgba(234, 179, 8, 1)';
        case 'extrapolated':
          return 'rgba(239, 68, 68, 1)';
        default:
          return 'rgba(107, 114, 128, 1)';
      }
    }),
    borderWidth: 1,
  };

  datasets.push(singleDataset);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { right: 28, left: 6, top: 4 },
    },
    elements: {
      bar: {
        clip: false,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Carbon Intensity of Electricity (gCO₂e/kWh)',
      },
      tooltip: {
        xAlign: 'right' as const,
        yAlign: 'center' as const,
        maxWidth: 360,
        padding: 12,
        boxPadding: 6,
        callbacks: {
          title: (context: any) => `Year: ${context[0].label}`,
          label: (context: any) => {
            const dataPoint = combinedData[context.dataIndex];
            return [
              `Carbon Intensity: ${dataPoint.value.toFixed(2)} gCO₂e/kWh`,
              'Source:',
              ...wrapTooltipProviderLines(dataPoint.provider, 48),
            ];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Carbon Intensity of Electricity (gCO₂e/kWh)',
        },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString(),
        },
      },
      x: {
        title: {
          display: true,
          text: 'Year',
        },
      },
    },
  };

  const latestYear = Math.max(...years.filter((year) => year <= 2023));
  const latestYearIndex = years.indexOf(latestYear);
  const latestDataPoint = combinedData[latestYearIndex];
  const latestValue = latestDataPoint.value;
  const latestProvider = latestDataPoint.provider;

  return {
    chartData: {
      labels: years,
      datasets,
    },
    chartOptions,
    latestValue: Math.round(latestValue * 100) / 100,
    latestYear,
    dataSource: latestProvider,
    providers: {
      cca: ccaProviders,
      egrid: egridProviders,
    },
    seriesByYear: years.map((year, i) => ({
      year,
      value: combinedData[i].value,
      source: combinedData[i].source as CarbonIntensityDataPoint['source'],
      provider: combinedData[i].provider,
    })),
  };
}

export async function fetchCarbonIntensityData(geofips: string): Promise<CarbonIntensityResult> {
  const years = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
  const gNum = Number(String(geofips).replace(/^0+/, '') || '0');

  if (isCaliforniaStateElectricityGeofips(gNum)) {
    const series = await fetchCaliforniaStateElectricityCarbonIntensitySeriesFromTable(supabaseClient, years);
    const byYear = new Map(series.map((p) => [p.year, p]));
    const combinedData: CombinedPoint[] = years.map((y) => {
      const hit = byYear.get(y);
      if (hit) {
        return {
          value: hit.value,
          source: hit.chartSource,
          provider: hit.provider,
        };
      }
      return {
        value: DEFAULT_STATE_CI_FALLBACK,
        source: 'extrapolated',
        provider: `Fallback ${DEFAULT_STATE_CI_FALLBACK} gCO₂e/kWh — no electricity_carbon_intensity row for ${y}`,
      };
    });
    return await assembleCarbonIntensityChartResult(years, combinedData, geofips, {
      stateFromElectricityTable: true,
    });
  }

  try {
    // Fetch CCAS data for the location
    const ccasData = await fetchCCASData(geofips);

    if (!ccasData) {
      throw new Error(`No CCAS data found for geofips: ${geofips}`);
    }
    
    // Get utility matches for eGRID
    const egridMatches = [
      ccasData.egrid_match1,
      ccasData.egrid_match2,
      ccasData.egrid_match3
    ].filter(match => match && match.trim() !== '');
    
    // Get CCA matches for LSE carbon intensity
    const ccaMatches = [
      ccasData.pcl_match1,
      ccasData.pcl_match2,
      ccasData.pcl_match3
    ].filter(match => match && match.trim() !== '');
    
    // Fetch data from both sources
    const [egridData, lseData] = await Promise.all([
      fetchEGridData(egridMatches, years),
      fetchLSECarbonIntensityData(ccaMatches, years)
    ]);
    
    // Process eGRID data (deterministic row if multiple subregions per year)
    const egridValues: (number | null)[] = years.map((year) => observedGco2eForYearFromEgridRows(egridData, year));

    // Process LSE data
    const lseValues: (number | null)[] = years.map((year) => meanLseGco2eForYear(lseData, year));

    const combinedData = buildCombinedCarbonIntensityPointsFirstPass(
      years,
      egridValues,
      lseValues,
      egridMatches[0] || 'eGRID Utility',
      ccasData.load_serving_entity
    );
    finalizeCombinedCarbonIntensitySeries(years, lseValues, combinedData, ccasData.load_serving_entity);

    return await assembleCarbonIntensityChartResult(years, combinedData as CombinedPoint[], geofips);
  } catch (error) {
    console.error('Error fetching carbon intensity data:', error);
    throw error;
  }
}
